import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import type { CustomerProfileFieldsQuery } from './graphql/Profile.generated';
import { useFormValidation } from './useFormValidation';

export const useProfileForm = (props: ProfileFormHook) => {
  const { customer } = props;
  const validationSchema = useFormValidation();

  // ──── Init Form ────────────────────────────────────────────────────────────

  const form = useForm<ProfileFormData>({
    defaultValues: {
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      email: customer?.email ?? '',
      phoneNumber: customer?.phoneNumber ?? '',
      password: '',
      hasPasswordChanged: false,
      confirmPassword: '',
    },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  // ──── Helpers ────────────────────────────────────────────────────────────────────

  const resetForm = React.useCallback(() => {
    form.reset({
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      email: customer?.email ?? '',
      phoneNumber: customer?.phoneNumber ?? '',
      password: '',
      hasPasswordChanged: false,
      confirmPassword: '',
    });
  }, [form, customer]);

  // ────────────────────────────────────────────────────────────────────

  return {
    form,
    resetForm,
    isSubmitAllowed: form.formState.isDirty,
  };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type ProfileFormData = {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly hasPasswordChanged: boolean;
  readonly password: string;
  readonly confirmPassword: string;
};

type ProfileFormHook = {
  readonly customer: CustomerProfileFieldsQuery['currentCustomer'];
};
